import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { DateTime } from "luxon";
import {
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  FileTextOutlined,
  CalendarOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import "./AppoinmentCalender.css";
import { doGet, doPatch } from "../../../../API/apis";
import { useStateGlobalContext } from "../../../../contexts/GlobalContext";
import { useOutletContext } from "react-router-dom";
import luxonPlugin from "@fullcalendar/luxon3";
import {
  Popover,
  Tag
} from "antd";
import TimeClock from "../../../../components/TimeClock/TimeClock";
import moment from "moment-timezone";
import Notification from "../../../../components/Notification/Notification";
import showConfirmModal from "../../../../components/ModalConfirm/ModalConfirm";
import { AppointmentStatus } from "../../../../components/GlobalComponents/StatusLabels";
import AppointmentModal from "../../../../components/AppointmentModal/AppointmentModal";
import ViewAppointmentModal from "../../../../components/AppointmentModal/ViewAppointmentModal";


// Add a helper function to format the appointment status with appropriate color
const getStatusTag = (status) => {
  const statusColors = {
    'Scheduled': 'blue',
    'Rescheduled': 'orange',
    'Completed': 'green',
    'Cancelled': 'red',
    'No Show': 'purple'
  };



  return <Tag color={statusColors[status] || 'blue'}>{status}</Tag>;
};

// Generate consistent colors for providers
const generateProviderColor = (providerId) => {
  // Array of distinct colors for providers
  const providerColors = [
    "#4285F4", // Blue
    "#EA4335", // Red
    "#FBBC05", // Yellow
    "#34A853", // Green
    "#8E24AA", // Purple
    "#F4511E", // Deep Orange
    "#00ACC1", // Cyan
    "#43A047", // Green
    "#E91E63", // Pink
    "#3949AB", // Indigo
    "#00897B", // Teal
    "#FFA000", // Amber
    "#D81B60", // Pink
    "#5E35B1", // Deep Purple
    "#039BE5", // Light Blue
  ];

  // Use modulo to ensure we have a color even if we have more providers than colors
  if (providerId) {
    // Convert string ID to a consistent number if necessary
    const numericId = typeof providerId === 'number'
      ? providerId
      : providerId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return providerColors[numericId % providerColors.length];
  }

  // Default color
  return providerColors[0];
};



function AppointmentCalendar() {
  const calendarRef = useRef(null);
  const [data, setData] = useState([]);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isVisible, setVisible] = useState(false);
  const [isViewVisible, setIsViewVisible] = useState(false);
  const [appointmentWith, setAppointmentWith] = useState(null);
  const [appointmentDate, setAppointmentDate] = useState(null);
  const [appointmentTime, setAppointmentTime] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [currentView, setCurrentView] = useState("dayGridMonth");
  const [modalFormData, setModalFormData] = useState({});
  const [viewAppointmentData, setViewAppointmentData] = useState(null);

  const [officeDateFormat, officeTimeFormat, officeTimezone] = useOutletContext();
  const { officeSetting, providers } = useStateGlobalContext();

  // Extract operating hours once
  const operatingHours = officeSetting?.operating_hrs?.[0] || {};
  const calendarOpeningTime = operatingHours?.start_time || "08:00";
  const calendarClosingTime = operatingHours?.end_time || "18:00";

  // Define getAppointments first before it's used in other functions
  const getAppointments = useCallback(async () => {
    if (!currentView?.startStr) return;

    setLoading(true);
    try {
      // Convert ISO String to Date Object and format dates
      const startDate = new Date(currentView.startStr).toISOString().slice(0, 10);
      const endDate = new Date(currentView.endStr).toISOString().slice(0, 10);

      const response = await doGet(
        `/reports/appointments/?start_date=${startDate}&end_date=${endDate}`
      );

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  }, [currentView]);



  // Memoize events to avoid recalculation on every render
  const events = useMemo(() => {
    return data?.map((appointment) => {
      const {
        id,
        first_name,
        middle_name,
        last_name,
        dob,
        gender,
        clinic,
        contact,
        appointment_with,
        appointment_date,
        service,
        visit_reason,
        duration,
        patient_id,
        provider_id
      } = appointment;

      const endTime = dayjs(appointment_date);
      const newEndTime = endTime.add(duration, "minute");
      const newTimeString = newEndTime.toISOString();
      const fullName = `${first_name || ''} ${middle_name || ''} ${last_name || ''}`.trim();

      return {
        id,
        title: `${fullName} - Appointment with ${appointment_with}`,
        start: appointment_date,
        end: newTimeString,
        first_name,
        middle_name,
        last_name,
        service_name: service,
        appointment_with,
        reason_for_visit: visit_reason,
        dob,
        gender,
        contact,
        clinic,
        duration,
        patient_id,
        current_status: appointment.current_status,
        provider_id,
        backgroundColor: generateProviderColor(provider_id),
        borderColor: generateProviderColor(provider_id)
      };
    }) || [];
  }, [data]);

  const handleDateClick = useCallback((selected) => {
    if (!selected) return;

    const date = selected.date;
    const selectedTime = DateTime.fromJSDate(date, { zone: officeTimezone });
    const formattedDate = selectedTime.toFormat("MM-dd-yyyy");

    // Create dayjs object for the selected time
    const dayjsTime = dayjs()
      .hour(selectedTime.hour)
      .minute(selectedTime.minute)
      .second(0);

    // Check if we're in month view and switch to day view
    if (calendarRef.current?.getApi()) {
      const calendarApi = calendarRef.current.getApi();
      const currentViewType = calendarApi.view.type;

      // If we're in month view, switch to day view for the clicked date
      if (currentViewType === 'dayGridMonth') {
        calendarApi.changeView('timeGridDay', date);
        return; // Exit early since we're switching views
      }
    }

    setAppointmentDate(formattedDate);
    setAppointmentTime(selectedTime.toFormat("h:mm a"));
    setEdit(false);
    setSelectedAppointment(null);
    setModalFormData({
      start_time: dayjsTime,
      appointment_date: dayjs(formattedDate),
    });
    setVisible(true);
  }, [officeTimezone]);

  const handleCancel = useCallback(() => {
    setVisible(false);
    setSelectedAppointment(null);
    setAppointmentDate(null);
    setAppointmentWith(null);
    setEdit(false);
    setModalFormData({});
  }, []);

  const handleCloseView = useCallback(() => {
    setIsViewVisible(false);
    setViewAppointmentData(null);
  }, []);

  const handleSwitchToEdit = useCallback(() => {
    setIsViewVisible(false);
    setEdit(true);
    setVisible(true);
  }, []);



  const handleEventClick = useCallback((selected) => {
    if (!selected?.event) return;

    const appointmentId = selected.event.id || selected.event._def.publicId;
    setSelectedAppointment(appointmentId);

    const { start, end } = selected.event;
    const {
      service_name,
      appointment_with,
      reason_for_visit,
      first_name,
      middle_name,
      last_name,
      patient_id,
      contact,
      dob,
      gender,
      current_status,
      provider_id
    } = selected.event.extendedProps;


    // Get the date and time
    const formattedDate = moment(start).tz(officeTimezone);

    // Create dayjs object for the selected time
    const dayjsTime = dayjs()
      .hour(formattedDate.hour())
      .minute(formattedDate.minute())
      .second(0);

    const patient_name = first_name && last_name ? `${first_name} ${middle_name || ''} ${last_name}`.trim() : "";

    setAppointmentDate(formattedDate);
    setAppointmentTime(formattedDate.format("h:mm a"));
    setAppointmentWith(appointment_with);

    // Prepare data for view modal
    const viewData = {
      id: appointmentId,
      first_name,
      middle_name,
      last_name,
      dob: dob ? moment(dob) : null,
      gender,
      contact,
      service_name,
      appointment_with,
      reason_for_visit: reason_for_visit || "",
      appointment_date: start,
      start_time: start,
      duration: moment(end).diff(moment(start), "minutes"),
      current_status,
      provider_id,
      patient_id
    };

    setViewAppointmentData(viewData);
    setIsViewVisible(true);

    // Also prepare data for edit modal (when user clicks edit)
    const commonFields = {
      appointment_date: dayjs(formattedDate),
      start_time: dayjsTime,
      duration: moment(end).diff(moment(start), "minutes"),
      service: service_name,
      appointment_with: appointment_with,
      reason_for_visit: reason_for_visit || "",
      original_appointment_date: start, // Preserve the original appointment date from the event
      current_status: current_status, // Preserve the original status
    };

    if (!patient_id) {
      setModalFormData({
        unregistered_patient: true,
        first_name,
        last_name,
        middle_name,
        phone: contact,
        dob: dob ? dayjs(dob) : null,
        ...commonFields,
      });
    } else {
      setModalFormData({
        patient_name,
        ...commonFields,
      });
    }
  }, [officeTimezone]);



  // Callback function to handle appointment success (creation/update)
  const handleAppointmentSuccess = useCallback(() => {
    getAppointments();
    handleCancel();
  }, [getAppointments, handleCancel]);

  const handleEventDrop = useCallback(async (eventDropInfo) => {
    // Get the event that was dropped
    const { event } = eventDropInfo;
    const appointmentId = event._def.publicId;

    // Get the original status from the event
    const originalStatus = event.extendedProps?.current_status || "Scheduled";

    // Get the new start and end times
    const newStart = event.start;
    const newEnd = event.end;

    // Format the new date and time for the API
    const newDateTime = moment(newStart).tz(officeTimezone);
    const hour24 = newDateTime.hour();
    const minute = newDateTime.minute();
    const timeString = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;

    // Calculate duration in minutes
    const durationMinutes = newEnd ? moment(newEnd).diff(moment(newStart), 'minutes') : 15;

    try {
      // Show confirmation dialog
      showConfirmModal({
        title: "Confirm Reschedule",
        content: `Are you sure you want to reschedule this appointment to ${newDateTime.format('MMM D, YYYY')} at ${newDateTime.format('h:mm A')}?`,
        okText: "Reschedule",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            // Prepare data for the API - preserve original status
            const data = {
              appointment_date: newDateTime.utc().format(),
              time_zone_value: officeTimezone,
              time: timeString,
              duration: durationMinutes,
              current_status: originalStatus
            };

            // Call the API to update the appointment
            const response = await doPatch(`/appointments/update/${appointmentId}/`, data);

            if (response.status === 200) {
              Notification.success("Appointment rescheduled successfully");
              getAppointments();
            } else {
              Notification.error("Failed to reschedule appointment");
              getAppointments(); // Refresh to revert the UI
            }
          } catch (error) {
            console.error("Error rescheduling appointment:", error);
            Notification.error("An error occurred while rescheduling");
            getAppointments(); // Refresh to revert the UI
          }
        },
        onCancel: () => {
          // Revert the change in the UI
          eventDropInfo.revert();
        }
      });
    } catch (error) {
      console.error("Error in handleEventDrop:", error);
      eventDropInfo.revert();
    }
  }, [officeTimezone, getAppointments]);

  const handleEventResize = useCallback(async (eventResizeInfo) => {
    // Get the event that was resized
    const { event } = eventResizeInfo;
    const appointmentId = event._def.publicId;

    // Get the original status from the event
    const originalStatus = event.extendedProps?.current_status || "Scheduled";

    // Get the new start and end times
    const newStart = event.start;
    const newEnd = event.end;

    // Calculate new duration in minutes
    const durationMinutes = moment(newEnd).diff(moment(newStart), 'minutes');

    try {
      // Show confirmation dialog
      showConfirmModal({
        title: "Confirm Duration Change",
        content: `Are you sure you want to change this appointment duration to ${durationMinutes} minutes?`,
        okText: "Update",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            // Prepare data for the API - preserve original status
            const data = {
              duration: durationMinutes,
              current_status: originalStatus
            };

            // Call the API to update the appointment
            const response = await doPatch(`/appointments/update/${appointmentId}/`, data);

            if (response.status === 200) {
              Notification.success("Appointment duration updated successfully");
              getAppointments();
            } else {
              Notification.error("Failed to update appointment duration");
              getAppointments(); // Refresh to revert the UI
            }
          } catch (error) {
            console.error("Error updating appointment duration:", error);
            Notification.error("An error occurred while updating duration");
            getAppointments(); // Refresh to revert the UI
          }
        },
        onCancel: () => {
          // Revert the change in the UI
          eventResizeInfo.revert();
        }
      });
    } catch (error) {
      console.error("Error in handleEventResize:", error);
      eventResizeInfo.revert();
    }
  }, [getAppointments]);


  // Fetch appointments when view changes
  useEffect(() => {
    if (currentView?.startStr && currentView?.endStr) {
      getAppointments();
    }
  }, [currentView?.startStr, currentView?.endStr, getAppointments]);



  // Enhanced event render function with better tooltip
  const renderEventContent = useCallback((eventInfo) => {
    const { event } = eventInfo;
    const {
      first_name,
      middle_name,
      last_name,
      current_status,
      provider_id
    } = event.extendedProps;

    const patientName = `${first_name || ''} ${middle_name || ''} ${last_name || ''}`.trim();
    const startTime = moment(event.start).tz(officeTimezone).format('h:mm a');

    // Check if we're in list view
    const isListView = () => {
      if (typeof currentView === 'object' && currentView.view) {
        return currentView.view.type === 'listMonth';
      } else if (calendarRef.current?.getApi()) {
        return calendarRef.current.getApi().view.type === 'listMonth';
      }
      return false;
    };

    // Check if we're in month view
    const isMonthView = () => {
      if (typeof currentView === 'object' && currentView.view) {
        return currentView.view.type === 'dayGridMonth';
      } else if (calendarRef.current?.getApi()) {
        return calendarRef.current.getApi().view.type === 'dayGridMonth';
      }
      return false;
    };

    // Get color based on provider id
    const providerColor = generateProviderColor(provider_id);

    // In month view, just return the basic content without Popover to improve performance
    if (isMonthView()) {
      return (
        <div className="fc-event-main-wrapper" style={{ borderLeft: `3px solid ${providerColor}` }}>
          <div className="fc-event-title-container">
            <div className="fc-event-title fc-sticky">
              {`${patientName} - ${startTime}`}
            </div>
          </div>
        </div>
      );
    }

    // Generate the tooltip content function - only gets called when hovering
    const generateTooltipContent = () => {
      const {
        service_name,
        appointment_with,
        reason_for_visit,
        contact,
        dob,
        gender,
      } = event.extendedProps;

      const endTime = moment(event.end).tz(officeTimezone).format('h:mm a');
      const appointmentDate = moment(event.start).tz(officeTimezone).format('MMM DD, YYYY');

      return (
        <div className="appointment-tooltip">
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '14px' }}>{patientName}</strong>
              <AppointmentStatus status={current_status} />
            </div>
            {dob && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                DOB: {moment(dob).format('MM/DD/YYYY')} • {gender || 'Unknown'}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', marginBottom: '8px' }}>
            <div style={{ width: '24px', marginRight: '8px', color: '#1890ff' }}>
              <CalendarOutlined />
            </div>
            <div>
              <div>{appointmentDate}</div>
            </div>
          </div>

          <div style={{ display: 'flex', marginBottom: '8px' }}>
            <div style={{ width: '24px', marginRight: '8px', color: '#1890ff' }}>
              <ClockCircleOutlined />
            </div>
            <div>
              <div>{startTime} - {endTime}</div>
            </div>
          </div>

          <div style={{ display: 'flex', marginBottom: '8px' }}>
            <div style={{ width: '24px', marginRight: '8px', color: '#1890ff' }}>
              <UserOutlined />
            </div>
            <div>
              <div><strong>Provider:</strong> {appointment_with}</div>
              <div><strong>Service:</strong> {service_name}</div>
            </div>
          </div>

          {contact && (
            <div style={{ display: 'flex', marginBottom: '8px' }}>
              <div style={{ width: '24px', marginRight: '8px', color: '#1890ff' }}>
                <PhoneOutlined />
              </div>
              <div>{contact}</div>
            </div>
          )}

          {reason_for_visit && (
            <div style={{ display: 'flex', marginBottom: '8px' }}>
              <div style={{ width: '24px', marginRight: '8px', color: '#1890ff' }}>
                <FileTextOutlined />
              </div>
              <div>
                <div><strong>Reason:</strong></div>
                <div style={{ fontSize: '12px' }}>{reason_for_visit}</div>
              </div>
            </div>
          )}
        </div>
      );
    };

    // Determine the best placement based on the view
    const getPopoverPlacement = () => {
      // Get current calendar view
      let calendarViewType;

      if (typeof currentView === 'object' && currentView.view) {
        calendarViewType = currentView.view.type;
      } else if (calendarRef.current?.getApi()) {
        calendarViewType = calendarRef.current.getApi().view.type;
      } else {
        calendarViewType = typeof currentView === 'string' ? currentView : 'dayGridMonth';
      }

      // For list view, center the popover
      if (calendarViewType === 'listMonth') {
        return 'top';
      }

      if (calendarViewType === 'timeGridWeek') {
        return 'right';
      }

      // For day views, always place popover on top to prevent hiding appointments
      if (calendarViewType === 'timeGridDay') {
        return 'top';
      }

      return 'rightTop';
    };

    // For list view
    if (isListView()) {
      return (
        <Popover
          content={generateTooltipContent}
          title={`Appointment Details`}
          trigger="hover"
          overlayClassName="appointment-popover list-view-popover"
          placement="top"
          mouseEnterDelay={0.3}
          mouseLeaveDelay={0.2}
          overlayStyle={{ maxWidth: '350px' }}
          getPopupContainer={() => document.body}
          arrow={true}
          destroyTooltipOnHide={true}
          zIndex={1100}
        >
          <div className="fc-list-event-title">
            {event.title}
          </div>
        </Popover>
      );
    }

    // For other views (timeGridDay, timeGridWeek)
    return (
      <Popover
        content={generateTooltipContent}
        title={`Appointment Details`}
        trigger="hover"
        overlayClassName="appointment-popover"
        placement={getPopoverPlacement()}
        mouseEnterDelay={0.3}
        mouseLeaveDelay={0.2}
        overlayStyle={{ maxWidth: '350px' }}
        getPopupContainer={() => document.body}
        arrow={true}
        destroyTooltipOnHide={true}
        zIndex={1100}
      >
        <div className="fc-event-main-wrapper" style={{ borderLeft: `3px solid ${providerColor}` }}>
          <div className="fc-event-title-container">
            <div className="fc-event-title fc-sticky">
              {event.title}
            </div>
          </div>
        </div>
      </Popover>
    );
  }, [officeTimezone, currentView]);

  // Handle event mounting to ensure popovers work in all views
  const handleEventDidMount = useCallback((info) => {
    const { el, view, event } = info;

    // Add specific handling for list view
    if (view.type === 'listMonth') {
      // Find the title element in the list view
      const titleEl = el.querySelector('.fc-list-event-title');
      if (titleEl) {
        // Make sure the title is clickable
        titleEl.style.cursor = 'pointer';

        // Add a class to help with styling
        el.classList.add('fc-list-event-with-popover');
      }
    }

    // For month view, optimize rendering and add click event
    if (view.type === 'dayGridMonth') {
      // Add cursor pointer to indicate clickability
      requestAnimationFrame(() => {
        el.style.cursor = 'pointer';

        // Set a data attribute to identify the event by ID
        el.setAttribute('data-event-id', event.id);

        // Reduce DOM operations by minimizing style changes
        // Add minimal styles directly
        el.style.overflow = 'hidden';
        el.style.textOverflow = 'ellipsis';
        el.style.whiteSpace = 'nowrap';

        // Use simple class instead of complex style manipulations
        el.classList.add('month-view-event');
      });
    }
  }, []);

  // Additional function to handle month view event click and show details
  const handleMonthViewEventClick = useCallback((eventClickInfo) => {
    // If we're in month view, show a modal with details instead of editing
    if (currentView?.view?.type === 'dayGridMonth') {
      // Instead of showing popover, trigger the event click handler directly
      // This will open the appointment modal with all details
      handleEventClick(eventClickInfo);
    } else {
      // For other views, use the default event click handler
      handleEventClick(eventClickInfo);
    }
  }, [currentView, handleEventClick]);

  return (
    <div className="calender mb-5">
      <TimeClock />
      <div className="flex justify-center mt-4">
        {/* Add a legend for provider colors */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '15px' }}>
          {providers?.map(provider => (
            <div key={provider.id} style={{ margin: '0 10px', display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: generateProviderColor(provider.id),
                marginRight: '5px'
              }} />
              <span style={{ fontSize: '12px' }}>{provider.name}</span>
            </div>
          ))}
        </div>
      </div>
      <FullCalendar
        ref={calendarRef}
        height="70vh"
        timeZone={officeTimezone || "America/Central"}
        datesSet={(info) => setCurrentView(info)}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          listPlugin,
          luxonPlugin,
        ]}
        nowIndicator={true}
        allDaySlot={false}
        headerToolbar={{
          left: "prev,today,next",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
        }}
        slotDuration="00:15:00"
        slotMinTime={calendarOpeningTime}
        slotMaxTime={calendarClosingTime}
        initialView="timeGridWeek"
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={3} // Limit visible events in month view 
        moreLinkClick="popover" // Show more link as popover instead of new view
        dayMaxEventRows={3} // Limit event rows in month view
        lazyFetching={true} // Optimize for lazy loading events
        progressiveEventRendering={true} // Render events progressively for better UI responsiveness
        dateClick={handleDateClick}
        eventClick={handleMonthViewEventClick}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        eventDidMount={handleEventDidMount}
        events={events}
        eventContent={renderEventContent}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short'
        }}
        forceEventDuration={true}
      />

      <AppointmentModal
        isVisible={isVisible}
        onClose={handleCancel}
        edit={edit}
        selectedAppointment={selectedAppointment}
        appointmentDate={appointmentDate}
        appointmentTime={appointmentTime}
        appointmentWith={appointmentWith}
        providers={providers}
        officeTimezone={officeTimezone}
        onAppointmentSuccess={handleAppointmentSuccess}
        initialFormData={modalFormData}
      />

      <ViewAppointmentModal
        isVisible={isViewVisible}
        onClose={handleCloseView}
        onEdit={handleSwitchToEdit}
        appointmentData={viewAppointmentData}
        officeTimezone={officeTimezone}
        onStatusUpdate={getAppointments}
      />
    </div>
  );
}

export default AppointmentCalendar;
